import { ClaimTypesEnum } from '../enums/claim-types.enum';
import { ClaimValue } from '../enums/claim-value.enum';

/**
 * SA-21604 / SA-21774 / SA-21932: where an account keeps the email address the customer
 * can actually be reached on.
 *
 * `contact_email` is the address the customer registered, so it wins wherever the platform
 * kept one. The `email` claim is a real address only on an account that neither carries a
 * codice fiscale - an Italian account logs in with the fiscal code and keeps a
 * system-generated placeholder in `email` - nor is flagged `no_email = true`. Every other
 * account has no address at all and has to read blank rather than show a placeholder.
 *
 * A migrated account can reach the UI with neither of those set and a `…+temp@synlab.com`
 * address sitting in the claim anyway (SA-21932), so whichever claim the rule lands on is
 * checked for that placeholder before it is handed back.
 */

/**
 * Minimal shape shared by every `Claim` type in the app (`models/claims.model`,
 * `synlab-access-ui-components/core`), so callers can pass whichever they hold.
 */
type ClaimLike = { claimType: string; claimValue: string };

/** Claims as they reach the UI: either the raw list or the flattened object. */
export type ClaimSource = readonly ClaimLike[] | Record<string, any> | null | undefined;

/** The value of a single claim, or an empty string when the account does not carry it. */
function claimValue(claims: ClaimSource, claimType: string): string {
  if (!claims) return '';

  const value = Array.isArray(claims)
    ? claims.find(claim => claim?.claimType === claimType)?.claimValue
    : (claims as Record<string, any>)[claimType];

  return typeof value === 'string' ? value : '';
}

/** Suffix the back end gives accounts created without a real address: `{guid}+temp@synlab.com`. */
const TEMP_EMAIL_SUFFIX = '+temp@synlab.com';

/**
 * Whether an address *is* the system-generated placeholder. Distinct from
 * {@link hasPlaceholderEmail}, which reads the account's `no_email` flag: a migrated account
 * can hold a `…+temp@synlab.com` address without that flag ever being set (SA-21932).
 */
export function isPlaceholderEmail(email: string | undefined | null): boolean {
  return (email || '').toLowerCase().endsWith(TEMP_EMAIL_SUFFIX);
}

/** An Italian account is one that carries a codice fiscale. */
export function isItalianAccount(claims: ClaimSource): boolean {
  return Boolean(claimValue(claims, ClaimTypesEnum.FISCAL_CODE));
}

/** Whether the `email` claim holds a placeholder rather than a real address. */
export function hasPlaceholderEmail(claims: ClaimSource): boolean {
  return claimValue(claims, ClaimTypesEnum.NO_EMAIL).toLowerCase() === ClaimValue.TRUE;
}

/**
 * The claim an edited email address has to be written back to. Only Italian accounts
 * maintain `contact_email`; for everyone else the address still lives in `email`.
 */
export function emailClaimType(claims: ClaimSource): ClaimTypesEnum {
  return isItalianAccount(claims) ? ClaimTypesEnum.CONTACT_EMAIL : ClaimTypesEnum.EMAIL;
}

/** The email address on file, or an empty string when the account has none. */
export function accountEmail(claims: ClaimSource): string {
  const contactEmail = claimValue(claims, ClaimTypesEnum.CONTACT_EMAIL).trim();
  if (contactEmail) return isPlaceholderEmail(contactEmail) ? '' : contactEmail;

  // with no contact address left to read, `email` is a placeholder on an Italian account and
  // on one flagged `no_email`, and those are exactly the accounts with no address (SA-21774)
  if (isItalianAccount(claims) || hasPlaceholderEmail(claims)) return '';

  const email = claimValue(claims, ClaimTypesEnum.EMAIL).trim();

  // a migrated account carries the placeholder without ever being flagged (SA-21932)
  return isPlaceholderEmail(email) ? '' : email;
}

/**
 * Whether the account has no address to reach the customer on - the `no_email = true` case,
 * which an Italian account reaches by having no `contact_email`.
 */
export function hasNoAccountEmail(claims: ClaimSource): boolean {
  return !accountEmail(claims);
}
